/**
 * The charity total, on the wall, for a presentation.
 *
 * This is the button somebody presses while standing next to the screen with a
 * cheque in their hand. Everything about it follows from that:
 *
 *   - THE FIGURE IS TYPED. total-raised.js explains why at length: real totals
 *     include cash, card and raffle money that never touches this system, so
 *     there is nothing here to derive it from and a computed number would be
 *     confidently wrong in front of the people who raised it.
 *   - THE BUTTON STATE IS READ BACK FROM THE ROOM, not from what this panel
 *     last sent. `total_raised_changed` is in the MUTATING set in
 *     tournamentRoom.js, so a takeover started from another admin's phone —
 *     or stopped by the draw running — shows up here without a reload.
 *   - IT DOES NOT RECORD ANYTHING. Writing the figure into the season's charity
 *     record is a separate act on the Charity page, deliberately: a presenter
 *     nudging the number mid-presentation must not rewrite the season's
 *     history, and taking the takeover off the wall must not un-record what was
 *     raised.
 *
 * HIDDEN UNLESS THE VENUE HAS TURNED IT ON (tv_total_raised_enabled, off by
 * default). A venue not running a charity has no use for a control that puts a
 * fundraising total over the darts, and a permanently-present one is a button
 * somebody eventually presses to see what it does.
 */

import { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api.js';
import { tvToggles } from '../tv/TvAds.jsx';

export default function TotalRaisedPanel({ venueId, tournamentId, snapshot }) {
  const [enabled, setEnabled] = useState(false);
  const [amount, setAmount]   = useState('');
  const [label, setLabel]     = useState('');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);

  // The venue's own toggle, from the public settings — the same read the TV
  // does. Fetched here rather than threaded down from the page because this is
  // the only thing on that page which needs it, and it is one cached GET.
  useEffect(() => {
    if (venueId == null) return undefined;

    let cancelled = false;

    (async () => {
      const { data } = await apiGet(`/api/venues/${venueId}/settings/public`);
      if (!cancelled) setEnabled(tvToggles(data?.settings).totalRaised);
    })();

    return () => { cancelled = true; };
  }, [venueId]);

  const live = snapshot?.totalRaised ?? null;

  // When a takeover is already up, the boxes show what is ON THE WALL rather
  // than whatever was last typed — an admin arriving at this page mid-
  // presentation needs to see the number the room is showing, and editing from
  // it is how you correct a typo that is currently two metres wide.
  useEffect(() => {
    if (live) {
      setAmount(String(live.amount ?? ''));
      setLabel(live.label ?? '');
    }
  }, [live]);

  if (!enabled) return null;

  const send = async (body) => {
    setBusy(true);
    setError(null);

    const { error: err } = await apiPost(
      `/api/venues/${venueId}/tournaments/${tournamentId}/total-raised`,
      body
    );

    setBusy(false);
    if (err) setError(err);

    // No local state change on success: the room broadcasts, the snapshot
    // arrives, and `live` moves. One source of truth for what the wall shows.
  };

  const start = () => {
    const raw = amount.trim();

    // EMPTY IS NOT ZERO, and nothing downstream catches this. Number('') is 0,
    // which is finite and not negative, so an empty box sails through every
    // check here AND the endpoint's — and puts "£0" across a pub wall in front
    // of the people who just raised the money. An admin who genuinely means
    // zero can type it.
    if (raw === '') {
      setError('Enter the amount first — an empty box is not £0.');
      return;
    }

    const value = Number(raw);

    // Checked here as well as server-side so the message names the box rather
    // than coming back as a rejected request.
    if (!Number.isFinite(value) || value < 0) {
      setError('Enter the amount as a number, for example 1250 or 1250.50');
      return;
    }

    send({ amount: value, label: label.trim() || null });
  };

  return (
    <section className="hd-card hd-stack">
      <div className="hd-row-between">
        <div>
          <h2 className="hd-section-title" style={{ margin: 0 }}>Total raised</h2>
          <p className="hd-muted" style={{ margin: 0 }}>
            {live
              ? 'On the TV now, over everything else.'
              : 'Puts a figure over the whole screen for a presentation.'}
          </p>
        </div>

        {live && <span className="hd-badge hd-badge-accent">Showing</span>}
      </div>

      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <div className="hd-field">
        <label className="hd-label" htmlFor="total-raised-amount">Amount (£)</label>
        <input
          id="total-raised-amount"
          className="hd-input"
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          value={amount}
          disabled={busy}
          placeholder="1250"
          onChange={(e) => setAmount(e.target.value)}
        />
      </div>

      <div className="hd-field">
        <label className="hd-label" htmlFor="total-raised-label">Caption</label>
        <input
          id="total-raised-label"
          className="hd-input"
          type="text"
          maxLength={120}
          value={label}
          disabled={busy}
          placeholder="Total raised"
          onChange={(e) => setLabel(e.target.value)}
        />
        <p className="hd-hint">
          Sits above the figure. Leave it empty for “Total raised”.
        </p>
      </div>

      <div className="hd-row">
        <button
          type="button"
          className="hd-btn hd-btn-primary"
          disabled={busy}
          onClick={start}
        >
          {/* Re-sending while it is up is how a typo gets corrected, so the
              button says so rather than going disabled and leaving a wrong
              number on the wall with no way back to it. */}
          {busy ? 'Working…' : live ? 'Update the screen' : 'Show on TV'}
        </button>

        {live && (
          <button
            type="button"
            className="hd-btn hd-btn-secondary"
            disabled={busy}
            onClick={() => send({ active: false })}
          >
            Take it off
          </button>
        )}
      </div>

      <p className="hd-hint">
        This shows a number; it does not record one. The season’s figure is
        saved on the Charity page. The takeover also comes off by itself when
        the draw runs.
      </p>
    </section>
  );
}
